import styled from '@emotion/styled';
import { Formik } from 'formik';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { generatePath, Link, useNavigate } from 'react-router-dom';

import { patientRoute } from 'App/common/routeLookup';
import { useUserContext } from 'App/contexts/UserContext';
import { saveStatus } from 'App/services/AMSService';
import { formatUtcDate } from 'App/services/DateService';
import { shouldExecutePatientSearch, shouldExecuteVisitSearch } from 'App/services/EhrService';
import PESService from 'App/services/PESService';
import { isExpired } from 'App/utils';
import { branchSettingsPropType, patientPropType } from 'Shared/types';
import FormButtonGroup from '../styles/FormButtonGroup';

import SelectField from 'Shared/form/SelectField';
import ActivityLocking from './ActivityLocking';
import AssessmentGeneratePDF from './AssessmentGeneratePDF';
import AssessmentScoring from './AssessmentScoring';
import ConfirmDeleteAssessmentModal from './ConfirmDeleteAssessmentModal';
import NewSummaryNote from './NewSummaryNote';
import SummaryNotesDisplay from './SummaryNotesDisplay';
import { scrollContainerId } from './constants';
import EhrEncounterNoteModal from './ehr/EhrEncounterNoteModal';
import AssessmentQuestion from './question/AssessmentQuestion';
import AssessmentQuestionErrorDisplay from './question/AssessmentQuestionErrorDisplay';
import AssessmentQuestionGroup from './question/AssessmentQuestionGroup';
import AssessmentSpecialQuestions from './question/AssessmentSpecialQuestions';
import { activityPropType, questionGroupPropType, questionPropType } from './types';
import convertAssessmentQuestionToView from './utils/convertAssessmentQuestionToView';
import getViewModeOptions from './utils/getViewModeOptions';
import useIsPermitted from 'App/utils/hooks/useIsPermitted';
import { PERMISSION_TYPES } from 'App/utils/isPermitted';

const FormStyle = styled.form`
    flex: 1 10 100%;
    padding: 20px;
    overflow: auto;
`;

const AssessmentTitleStyle = styled.div`
    display: flex;
    justify-content: space-between;
`;

const AssessmentName = styled.h1`
    font-size: 20px;
    margin-bottom: 20px;
`;

const AssessmentToolbar = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

function getDefaultViewMode(verbose, isPreview) {
    const options = getViewModeOptions(isPreview);
    return verbose
        ? options[0] // Fully Scripted
        : options[1]; // Compact
}

const AssessmentForm = ({
    activity,
    allQuestions,
    questionsWithChildren,
    splitQuestions,
    questionGroups,
    patient,
    branchSettings,
    isPreview,
}) => {
    const navigate = useNavigate();
    const user = useUserContext();

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [ehrModalOpen, setEhrModalOpen] = useState(false);
    const [isEhrResubmit, setIsEhrResubmit] = useState(false);
    const [patientEhrEligible, setPatientEhrEligible] = useState(true);

    const isActivityComplete = activity.status === 1;
    const isActivityDeleted = activity.status === 2;

    const activityEhrEligible = !!branchSettings.ehrType && !!activity.assessment.settings?.settings.ehrEncounterNote;
    const userEhrEligible = branchSettings.noteRequiresEhrProviderId !== true || !!user.user_attributes.ehrProviderId;
    const activePccOnly = useIsPermitted(PERMISSION_TYPES.ACTIVE_PCC_ONLY);
    const isClinicalActivityAndPccUser = activePccOnly && activity.assessment.defaultNoteType === 1;
    const isPDFGenerationEnabled = useIsPermitted(PERMISSION_TYPES.PDF_GENERATION);

    const isPatientEhrEligibleByIdentifiers = useCallback(async () => {
        if (!patient.id) {
            return true;
        }
        const identifiers = await PESService.getIdentifiers(patient.id);
        const executeIdentifierSearch = shouldExecutePatientSearch(Object.values(identifiers), branchSettings);
        if (!executeIdentifierSearch) {
            return true;
        }
        // silently catch errors from EHR search, this shouldn't influence our eligibility checks
        // error codes from EHR searches can be legitimate responses, so don't block the activity
        // if that occurs, and calculate based on the existence of identifiers
        const ehrIdentifiers = await PESService.getPatientIdentifiersFromEhr(patient.id).catch(() => null);
        return !isEmpty(ehrIdentifiers);
    }, [branchSettings, patient.id]);

    const isPatientEhrEligibleByVisits = useCallback(async () => {
        if (!branchSettings.ehrNoteRequiresVisit) {
            return true;
        }
        if (!patient.id) {
            return true;
        }
        const visits = await PESService.getVisits(patient.id);
        const executeVisitSearch = shouldExecuteVisitSearch(visits, branchSettings);
        if (!executeVisitSearch) {
            return true;
        }
        // silently catch errors from EHR search, this shouldn't influence our eligibility checks
        // error codes from EHR searches can be legitimate responses, so don't block the activity
        // if that occurs, and calculate based on the existence of visits
        const ehrVisits = await PESService.getPatientVisitsFromEhr(patient.id).catch(() => null);
        return !isEmpty(ehrVisits || visits);
    }, [branchSettings, patient.id]);

    const isPatientEhrEligible = useCallback(async () => {
        if (!activityEhrEligible) {
            return false;
        }
        const isEligibleByIdentifiers = await isPatientEhrEligibleByIdentifiers();
        const isEligibleByVisits = await isPatientEhrEligibleByVisits();
        return isEligibleByIdentifiers && isEligibleByVisits;
    }, [activityEhrEligible, isPatientEhrEligibleByIdentifiers, isPatientEhrEligibleByVisits]);

    useEffect(() => {
        isPatientEhrEligible().then(setPatientEhrEligible);
    }, [isPatientEhrEligible]);

    const initialValues = useMemo(() => {
        return {
            ...questionsWithChildren.reduce((agg, question) => {
                return {
                    ...agg,
                    [question.questionId]: convertAssessmentQuestionToView(question),
                };
            }, {}),
            viewMode: getDefaultViewMode(user.verbose, isPreview),
            newSummaryNote: '',
            newSummaryNoteClinicalIssues: [],
        };
    }, [isPreview, questionsWithChildren, user.verbose]);

    function saveSummaryNote(values) {
        if (!values.newSummaryNote && !values.newSummaryNoteClinicalIssues.length) {
            return;
        }

        return PESService.createSummaryNote({
            patientId: patient.id,
            activityId: activity.id,
            noteBody: values.newSummaryNote,
            clinicalIssues: values.newSummaryNoteClinicalIssues.map((issue) => issue.valueName),
            noteSubject: activity.assessment.name,
            noteType: activity.assessment.defaultNoteType,
        });
    }

    function goToPatientProfile() {
        navigate(generatePath(patientRoute, { patientId: patient.id }));
    }

    async function pendAssessment(values) {
        if (!patient.id) return;

        await saveSummaryNote(values);
        await saveStatus(activity.id, 'Pending');

        goToPatientProfile();
    }

    async function completeAssessment(values) {
        if (!patient.id) return;

        await saveSummaryNote(values);
        await saveStatus(activity.id, 'Finished');

        goToPatientProfile();
    }

    const openDeleteModal = () => {
        if (!patient.id) return;
        setDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = async (confirm, values) => {
        if (confirm) {
            await saveSummaryNote(values);
            await saveStatus(activity.id, 'Deleted');

            goToPatientProfile();
        }
        setDeleteModalOpen(false);
    };

    function openEhrEncounterNoteModal(resubmit = false) {
        setEhrModalOpen(true);
        setIsEhrResubmit(resubmit);
    }

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={(values) => {
                if (activityEhrEligible && patientEhrEligible && userEhrEligible) {
                    openEhrEncounterNoteModal();
                } else {
                    return completeAssessment(values);
                }
            }}
        >
            {({ handleSubmit, submitCount, isValid, errors, values, isSubmitting, setSubmitting }) => (
                <FormStyle onSubmit={handleSubmit} id={scrollContainerId}>
                    {isExpired(activity.assessment.retiredOn) && (
                        <Alert variant="danger">
                            <i className="fa fa-fw fa-info-circle" /> Therigy retired this activity on{' '}
                            {formatUtcDate({ date: activity.assessment.retiredOn, format: 'MMMM D, YYYY' })} and will no
                            longer support or update its contents.
                        </Alert>
                    )}

                    <AssessmentTitleStyle>
                        <AssessmentName>{activity.name}</AssessmentName>

                        {isPDFGenerationEnabled && <AssessmentGeneratePDF activity={activity} patient={patient} />}
                    </AssessmentTitleStyle>

                    {isActivityComplete && activity.completedByUser && activity.completedOn && (
                        <div>
                            <p>
                                This assessment was completed on{' '}
                                {formatUtcDate({ date: activity.completedOn, format: 'MMMM D, YYYY' })} by{' '}
                                {activity.completedByUser.firstName} {activity.completedByUser.lastName}.
                            </p>
                            {!!branchSettings.ehrType && (
                                <>
                                    <div>
                                        {activityEhrEligible
                                            ? 'Eligible for EHR Note: Yes'
                                            : 'Eligible for EHR Note: No'}
                                    </div>
                                    <div>
                                        {activityEhrEligible &&
                                        activity.activityEhrNotes &&
                                        activity.activityEhrNotes.sentOn &&
                                        !activity.activityEhrNotes.failed
                                            ? 'Note Sent to EHR: Yes'
                                            : 'Note Sent to EHR: No'}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    <AssessmentToolbar>
                        <div>
                            <i className="fa fa-fw fa-asterisk text-danger" /> Indicates question is required
                        </div>
                        <div
                            style={{
                                width: 200,
                            }}
                        >
                            <SelectField
                                fieldName="viewMode"
                                options={getViewModeOptions(isPreview)}
                                aria-label="View Mode"
                            />
                        </div>
                    </AssessmentToolbar>

                    <AssessmentSpecialQuestions
                        specialQuestions={splitQuestions.specialQuestions}
                        activity={activity}
                        patient={patient}
                    />

                    <SummaryNotesDisplay patient={patient} activity={activity} />

                    {splitQuestions.questions
                        .sort((a, b) => a.attributes.position - b.attributes.position)
                        .map((question) => (
                            <AssessmentQuestion
                                key={question.questionId}
                                question={question}
                                activity={activity}
                                patient={patient}
                            />
                        ))}

                    {questionGroups.map((group) => (
                        <AssessmentQuestionGroup
                            key={group.id}
                            group={group}
                            questions={splitQuestions.groupedQuestions[group.id]}
                            activity={activity}
                            patient={patient}
                        />
                    ))}

                    {!!Object.values(activity.assessment.scoring || {}).length && (
                        <AssessmentScoring activity={activity} />
                    )}

                    {splitQuestions.therapySummaryNote.length > 0 && <NewSummaryNote activity={activity} />}

                    {submitCount > 0 && !isValid && (
                        <div>
                            <div className="text-danger">
                                The following required question(s) must be answered to complete this assessment. Click
                                the unanswered question(s) below to enter the required answer(s).
                            </div>
                            <ul>
                                {Object.keys(errors).map((questionId) => (
                                    <AssessmentQuestionErrorDisplay
                                        key={questionId}
                                        questionId={questionId}
                                        patient={patient}
                                    />
                                ))}
                            </ul>
                        </div>
                    )}

                    {isClinicalActivityAndPccUser && (activity.status === 0 || activity.status === 3) && (
                        <div
                            data-testid="clinical-pcc-user-activity"
                            id="clinical-pcc-incomplete"
                            className="text-danger"
                        >
                            Complete is unavailable to PCC users.
                        </div>
                    )}

                    {activityEhrEligible && !patientEhrEligible && (
                        <div data-testid="ehr-patient-identifiers" id="ehr-ineligible" className="text-danger">
                            This patient is not eligible to send EHR encounter notes.
                        </div>
                    )}
                    {activityEhrEligible && !userEhrEligible && (
                        <div data-testid="ehr-provider-id-missing" className="text-danger">
                            The encounter note cannot be sent until your EHR Provider ID is entered.{' '}
                            <Link
                                to={{
                                    pathname: '/myAccount',
                                    search: 'sendBackAfterSave=true',
                                }}
                            >
                                Click here to update.
                            </Link>
                        </div>
                    )}

                    <FormButtonGroup>
                        <Button
                            id="pend-activity-btn"
                            variant="outline-secondary"
                            disabled={isActivityComplete || isActivityDeleted}
                            onClick={() => pendAssessment(values)}
                        >
                            Pend
                        </Button>
                        <Button
                            id="complete-btn"
                            type="submit"
                            variant="primary"
                            disabled={
                                !activity.id ||
                                isSubmitting ||
                                isActivityComplete ||
                                isActivityDeleted ||
                                isClinicalActivityAndPccUser
                            }
                        >
                            Complete
                        </Button>
                        {activityEhrEligible &&
                            patientEhrEligible &&
                            (!activity.activityEhrNotes?.sentOn || activity.activityEhrNotes?.failed) &&
                            isActivityComplete && (
                                <Button variant="outline-secondary" onClick={() => openEhrEncounterNoteModal(true)}>
                                    Send EHR Note
                                </Button>
                            )}

                        <div style={{ flexGrow: 1 }} />

                        <Button
                            variant="outline-danger"
                            disabled={isActivityComplete || isActivityDeleted}
                            onClick={openDeleteModal}
                        >
                            Delete
                        </Button>
                    </FormButtonGroup>

                    {ehrModalOpen && (
                        <EhrEncounterNoteModal
                            activity={activity}
                            patient={patient}
                            allQuestions={allQuestions}
                            questionGroups={questionGroups}
                            branchSettings={branchSettings}
                            onClose={() => {
                                setSubmitting(false);
                                setEhrModalOpen(false);
                            }}
                            onComplete={() => {
                                if (isEhrResubmit) {
                                    return goToPatientProfile();
                                }
                                return completeAssessment(values);
                            }}
                        />
                    )}

                    {deleteModalOpen && (
                        <ConfirmDeleteAssessmentModal onClose={(confirm) => handleCloseDeleteModal(confirm, values)} />
                    )}

                    {activity.id && <ActivityLocking activity={activity} />}
                </FormStyle>
            )}
        </Formik>
    );
};

AssessmentForm.propTypes = {
    activity: activityPropType.isRequired,
    allQuestions: PropTypes.objectOf(questionPropType).isRequired,
    questionsWithChildren: PropTypes.arrayOf(questionPropType).isRequired,
    splitQuestions: PropTypes.shape({
        specialQuestions: PropTypes.arrayOf(questionPropType).isRequired,
        therapySummaryNote: PropTypes.arrayOf(questionPropType).isRequired,
        groupedQuestions: PropTypes.objectOf(PropTypes.arrayOf(questionPropType)).isRequired,
        questions: PropTypes.arrayOf(questionPropType).isRequired,
    }).isRequired,
    questionGroups: PropTypes.arrayOf(questionGroupPropType).isRequired,
    patient: patientPropType.isRequired,
    branchSettings: branchSettingsPropType.isRequired,
    isPreview: PropTypes.bool,
};

AssessmentForm.defaultProps = {
    isPreview: false,
};

export default AssessmentForm;
